import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Button,
} from "@mui/material";
import { Link } from "react-router-dom";

const BusinessSection = () => {
  const [mounted, setMounted] = React.useState(false);
  const [businessStories, setBusinessStories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  React.useEffect(() => {
    if (!mounted) return;
    
    const fetchBusinessStories = async () => {
      try {
        const response = await fetch(
          "https://ethioinsider-backend.onrender.com/api/articles/business"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch business stories");
        }
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          setBusinessStories(result.data.slice(0, 3));
        } else {
          throw new Error("Unexpected API response format");
        }
      } catch (err) {
        setError(err.message);
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessStories();
  }, [mounted]);

  if (!mounted) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4, mt:10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4, mt:10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography
        variant="body1"
        color="error"
        sx={{ textAlign: "center", py: 4 }}
      >
        Error loading business stories: {error}
      </Typography>
    );
  }

  if (businessStories.length === 0) {
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ textAlign: "center", py: 4 }}
      >
        No business stories available at the moment.
      </Typography>
    );
  }

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid xs={12} md={8}>
          <Card square elevation={1} sx={{ height: "100%" }}>
            <CardActionArea component={Link} to={`/articles/${businessStories[0]?._id}`}>
              <CardMedia
                component="img"
                height="300"
                image={
                  businessStories[0]?.imageUrl ||
                  "https://via.placeholder.com/800x450?text=Business"
                }
                alt={businessStories[0]?.title}
                sx={{ objectFit: "cover" }}
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/800x450?text=Business";
                }}
              />
              <CardContent>
                <Chip
                  label="Featured"
                  color="primary"
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="h6" gutterBottom>
                  {businessStories[0]?.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  paragraph
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {businessStories[0]?.excerpt || "No excerpt available"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(businessStories[0]?.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid xs={12} md={4}>
          <Stack spacing={2}>
            {businessStories.slice(1, 3).map((story) => (
              <Card key={`business-${story._id}`} square elevation={1}>
                <CardActionArea component={Link} to={`/article/${story._id}`}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={
                      story?.imageUrl ||
                      "https://via.placeholder.com/400x250?text=Business"
                    }
                    alt={story?.title}
                    sx={{ objectFit: "cover" }}
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/400x250?text=Business";
                    }}
                  />
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "3em",
                      }}
                    >
                      {story?.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(story?.createdAt).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </Grid>
      </Grid>

      {businessStories.length >= 3 && (
        <Box textAlign="center">
          <Button variant="outlined" component={Link} to="/business-news">
            View All Business News
          </Button>
        </Box>
      )}
    </>
  );
};
export default BusinessSection;
